import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { urlFor } from '../client';
import {
  getGamesByCoreWithPagination,
  searchGamesInCoreWithPagination,
} from '../utils/sanityQueries';
import {
  Typography,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Pagination,
  ListItemButton,
  TextField,
  Toolbar, // Importe Toolbar para o efeito sticky
  AppBar, // Importe AppBar para o efeito sticky,
  IconButton, // Importe IconButton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search'; // Importe SearchIcon
import Navbar from '../components/Navbar';

const Core = () => {
  const { core: coreSlug } = useParams();
  const [games, setGames] = useState([]);
  const [totalGames, setTotalGames] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false); // Inicialmente false
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [tempSearchTerm, setTempSearchTerm] = useState(''); // Novo estado para o termo de busca temporário
  const gamesPerPage = 10;
  const totalPages = Math.ceil(totalGames / gamesPerPage);

  useEffect(() => {
    const fetchGames = async () => {
      setError(null); // Limpa o erro a cada nova busca
      setLoading(true); // Ativa o loading no início da busca

      if (searchTerm.length >= 3) {
        const { games: fetchedGames, total } = await searchGamesInCoreWithPagination(
          coreSlug,
          searchTerm,
          1,
          gamesPerPage
        );
        if (fetchedGames) {
          setGames(fetchedGames);
          setTotalGames(total);
        } else {
          setGames([]); // Define games como array vazio se não encontrar resultados
          setTotalGames(0);
        }
      } else {
        const { games: fetchedGames, total } = await getGamesByCoreWithPagination(
          coreSlug,
          currentPage,
          gamesPerPage
        );
        if (fetchedGames) {
          setGames(fetchedGames);
          setTotalGames(total);
        } else {
          setError(`Erro ao buscar jogos do core: ${coreSlug}`);
        }
      }
      setLoading(false); // Desativa o loading após a busca (com ou sem resultados)
      window.scrollTo(0, 0);
    };

    fetchGames();
  }, [coreSlug, currentPage, gamesPerPage, searchTerm]);

  const handleChangePage = (event, value) => {
    setCurrentPage(value);
  };

  const handleTempSearchChange = (event) => {
    setTempSearchTerm(event.target.value);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault(); // Evita o reload padrão do form
    const trimmedSearchTerm = tempSearchTerm.trim();
    if (trimmedSearchTerm === '') {
      setSearchTerm(''); // Define searchTerm para vazio para carregar todos os jogos
    } else {
      setSearchTerm(trimmedSearchTerm); // Define searchTerm para o termo de busca
    }
    setCurrentPage(1); // Resetar para a primeira página ao fazer uma nova busca ou voltar ao inicial
  };

  return (
    <>
      <Box sx={{ pb: 14 }}>
        <AppBar
          position="sticky"
          color="transparent"
          elevation={1}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            bgcolor: '#121212',
          }}
        >
          <Toolbar
            sx={{
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              px: [2],
              pt: 2,
              pb: 1,
            }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                cursor: 'default', width: { xs: '100%', sm: 'auto' }, 
                mb: { sm: 0 }, lineHeight: 1, flexGrow: { sm: 1 } 
              }}
            >
              {coreSlug === 'atari2600' && 'Atari 2600'}
              {coreSlug === 'gb' && 'Game Boy'}
              {coreSlug === 'gbc' && 'Game Boy Color'}
              {coreSlug === 'n64' && 'Nintendo 64'}
              {coreSlug === 'nes' && 'Nintendo'}
              {coreSlug === 'segaMS' && 'Sega Master System'}
              {coreSlug === 'snes' && 'Super Nintendo'}
            </Typography>
            <Box
              component="form"
              onSubmit={handleSearchSubmit}
              noValidate
              sx={{
                width: { xs: '100%', sm: 'auto' },
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                mt: { xs: 2, sm: 0 }
              }}
            >
              <TextField
                placeholder="Buscar Jogo"
                variant="outlined"
                size="small"
                fullWidth={{ xs: true, sm: false }}
                value={tempSearchTerm}
                onChange={handleTempSearchChange}
                sx={{ mr: 1, width: '100%' }}
              />
              <IconButton type="submit" aria-label="buscar">
                <SearchIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>
        <Box sx={{ position: 'relative', minHeight: '60vh' }}> {/* Container relative */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography variant="h6" color="error" align="center">
              {error}
            </Typography>
          ) : games.length > 0 ? (
            <List sx={{ width: '100%', pt: 0 }}>
              {games.map((game) => (
                <ListItem key={game._id} disablePadding>
                  <ListItemButton
                    component={Link}
                    to={`/${coreSlug}/${game.slug?.current}`}
                    alignItems="center"
                    sx={{
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.08)',
                      },
                      mb: 1,
                      textDecoration: 'none',
                      color: 'inherit',
                      px: 2
                    }}
                  >
                    {game.box?.asset?.url && (
                      <ListItemAvatar sx={{ m: 'auto' }}>
                        <Avatar
                          src={urlFor(game.box).width(256).url()}
                          alt={game.name}
                          sx={{ width: {xs: 96, sm: 128 }, height: 'auto', borderRadius: 1, mr: 2, }}
                        />
                      </ListItemAvatar>
                    )}
                    <ListItemText
                      primary={game.name}
                      sx={{
                        '.MuiTypography-root': {
                          fontSize: { xs: '1rem', sm: '1.25rem' },
                          whiteSpace: 'nowrap', // Impede a quebra de linha
                          overflow: 'hidden',   // Esconde o texto que ultrapassa o container
                          textOverflow: 'ellipsis', // Adiciona "..." ao final do texto truncado
                        },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          ) : searchTerm.length >= 3 && !loading && !error ? (
            <Typography variant="subtitle1" align="center" sx={{ mt: 2 }}>
              Nenhum jogo encontrado para "{searchTerm}"
            </Typography>
          ) : !loading && games.length === 0 && !error ? (
            <Typography variant="subtitle1" align="center" sx={{ mt: 2 }}>
              Nenhum jogo cadastrado neste core.
            </Typography>
          ) : null}
          {totalGames > gamesPerPage && games.length > 0 && !loading && !error && (
            <Pagination
              count={totalPages}
              page={currentPage}
              variant="text"
              color="primary"
              onChange={handleChangePage}
              sx={{
                display: 'flex',
                justifyContent: 'center'
              }}
            />
          )}
        </Box>
      </Box>
      <Navbar />
    </>
  );
};

export default Core;